/**
 * 🧾📚 Receipts History Page — full-page browser of every scanned
 * grocery receipt with search, date-range filter, image detail,
 * and edit-and-resave for OCR corrections.
 *
 * Route: /receipts
 * Data:
 *   GET    /api/receipts           → list (no image blobs)
 *   GET    /api/receipts/{id}      → full detail incl. image
 *   PATCH  /api/receipts/{id}      → edit-and-resave parsed data
 *   DELETE /api/receipts/{id}      → hard delete
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import ReceiptScanner from "@/components/ReceiptScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(v, currency = "USD") {
  if (v == null || Number.isNaN(Number(v))) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(v));
  } catch {
    return `$${Number(v).toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const CATEGORY_ICONS = {
  produce: "🥦", protein: "🥩", dairy: "🥛", grain: "🌾",
  pantry: "🥫", snack: "🍿", beverage: "🥤", household: "🧴", other: "🛒",
};

export default function ReceiptsPage() {
  const { user, isAuthed } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [q,        setQ]        = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [detail,   setDetail]   = useState(null);   // full receipt (with image)
  const [detailBusy, setDetailBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/receipts?limit=200`);
      setReceipts(r.data?.receipts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load receipts.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAuthed) refresh(); }, [isAuthed]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return receipts.filter((r) => {
      const p = r.parsed || {};
      if (term) {
        const hay = `${p.store || ""} ${p.line_items?.map((li) => li.name).join(" ") || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      const capturedDay = (r.captured_at || "").slice(0, 10);
      const receiptDay  = p.date || capturedDay;
      if (dateFrom && receiptDay && receiptDay < dateFrom) return false;
      if (dateTo   && receiptDay && receiptDay > dateTo)   return false;
      return true;
    });
  }, [receipts, q, dateFrom, dateTo]);

  const openDetail = async (receiptId) => {
    setDetailBusy(true);
    try {
      const r = await axios.get(`${API}/receipts/${receiptId}`);
      setDetail(r.data);
      setEditMode(false);
      setEditDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open receipt.");
    } finally { setDetailBusy(false); }
  };

  const startEdit = () => {
    if (!detail?.parsed) return;
    // Deep-ish clone the parsed block for a safe edit buffer.
    setEditDraft(JSON.parse(JSON.stringify(detail.parsed)));
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!detail || !editDraft) return;
    try {
      const r = await axios.patch(`${API}/receipts/${detail.receipt_id}`, {
        parsed: editDraft,
      });
      toast.success("Receipt updated. Ledger totals recomputed.");
      // Update local state so the row + detail reflect immediately.
      setDetail({ ...detail, parsed: r.data.parsed });
      setEditMode(false);
      setEditDraft(null);
      setReceipts((prev) =>
        prev.map((row) => row.receipt_id === detail.receipt_id
          ? { ...row, parsed: r.data.parsed }
          : row));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const removeReceipt = async () => {
    if (!detail) return;
    if (!window.confirm("Delete this receipt? Ledger totals will be reversed.")) return;
    try {
      await axios.delete(`${API}/receipts/${detail.receipt_id}`);
      toast.success("Receipt deleted.");
      setReceipts((prev) => prev.filter((r) => r.receipt_id !== detail.receipt_id));
      setDetail(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">
          🔒 Sign in to open Cassandra&apos;s Ledger.
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="receipts-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-3xl font-black tracking-wide text-amber-300"
                 style={{ fontFamily: "Anton, sans-serif" }}>
              🧾 CASSANDRA&apos;S LEDGER
            </div>
            <div className="text-xs text-neutral-400 mt-1">
              Every receipt you&apos;ve ever scanned · edit-and-resave for OCR corrections
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/kitchen"
              className="text-xs px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-neutral-700 border border-neutral-700"
              data-testid="receipts-back-to-kitchen"
            >
              ← Kitchen
            </Link>
            <button
              onClick={() => setScannerOpen(true)}
              className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-black tracking-wide hover:brightness-110"
              data-testid="receipts-scan-new"
            >
              📷 Scan New
            </button>
          </div>
        </div>

        {/* Search + filters */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-5">
          <input
            type="search"
            placeholder="Search by store or item…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="sm:col-span-2 px-3 py-2 rounded-lg bg-black/40 border border-neutral-800 text-sm focus:border-amber-500/60 focus:outline-none"
            data-testid="receipts-search"
          />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 rounded-lg bg-black/40 border border-neutral-800 text-sm text-neutral-300"
            data-testid="receipts-date-from"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 rounded-lg bg-black/40 border border-neutral-800 text-sm text-neutral-300"
            data-testid="receipts-date-to"
          />
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-16">Loading your ledger…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="receipts-empty">
            <div className="text-5xl mb-2">🧾</div>
            <div className="text-neutral-400 text-sm">
              {receipts.length === 0
                ? "No receipts yet. Snap one from the Kitchen or Gym to get started."
                : "No receipts match those filters."}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((r) => {
              const p = r.parsed || {};
              return (
                <button
                  key={r.receipt_id}
                  onClick={() => openDetail(r.receipt_id)}
                  className="text-left rounded-xl border border-neutral-800 hover:border-amber-500/60 bg-black/40 hover:bg-black/60 p-4 transition"
                  data-testid={`receipt-row-${r.receipt_id}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="text-sm font-bold text-amber-200 truncate">
                      {p.store || "Unknown store"}
                    </div>
                    <div className="text-xs text-neutral-500 whitespace-nowrap">
                      {fmtDate(p.date || r.captured_at)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-neutral-400">
                    <span>{(p.line_items || []).length} items · from {r.captured_from || "kitchen"}</span>
                    <span className="text-amber-300 font-black text-base"
                          style={{ fontFamily: "Anton, sans-serif" }}>
                      {money(p.total, p.currency)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <DetailModal
          detail={detail}
          editMode={editMode}
          editDraft={editDraft}
          setEditDraft={setEditDraft}
          onClose={() => { setDetail(null); setEditMode(false); setEditDraft(null); }}
          onEdit={startEdit}
          onCancelEdit={() => { setEditMode(false); setEditDraft(null); }}
          onSave={saveEdit}
          onDelete={removeReceipt}
        />
      )}
      {detailBusy && !detail && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center text-amber-300 text-sm">
          Opening receipt…
        </div>
      )}

      <ReceiptScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        capturedFrom="kitchen"
        onSaved={() => refresh()}
      />
      <Footer />
    </div>
  );
}

/* ─────────── Detail + Edit modal ─────────── */
function DetailModal({ detail, editMode, editDraft, setEditDraft, onClose, onEdit, onCancelEdit, onSave, onDelete }) {
  const p = editMode ? editDraft : (detail.parsed || {});
  const currency = p.currency || "USD";

  const updateField = (field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };
  const updateLineItem = (idx, key, value) => {
    setEditDraft((prev) => {
      const arr = [...(prev.line_items || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return { ...prev, line_items: arr };
    });
  };
  const removeLineItem = (idx) => {
    setEditDraft((prev) => {
      const arr = [...(prev.line_items || [])];
      arr.splice(idx, 1);
      return { ...prev, line_items: arr };
    });
  };
  const addLineItem = () => {
    setEditDraft((prev) => ({
      ...prev,
      line_items: [...(prev.line_items || []), { name: "", qty: 1, price: 0, category: "other" }],
    }));
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="receipt-detail-modal"
    >
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-amber-500/40 bg-[#0f0b06]">
        <div className="flex items-center justify-between border-b border-amber-500/20 px-5 py-3">
          <div className="text-lg font-black tracking-wide text-amber-300"
               style={{ fontFamily: "Anton, sans-serif" }}>
            🧾 RECEIPT · {editMode ? "EDIT" : "DETAIL"}
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-xl px-2"
            data-testid="receipt-detail-close"
          >×</button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-4">
          {/* Image */}
          <div className="rounded-lg overflow-hidden border border-neutral-800 bg-black/40">
            {detail.image_base64
              ? <img
                  src={`data:${detail.image_mime || "image/jpeg"};base64,${detail.image_base64}`}
                  alt="Receipt"
                  className="w-full h-full object-contain max-h-[60vh]"
                />
              : <div className="p-8 text-center text-neutral-500 text-xs">Image unavailable.</div>}
          </div>

          {/* Fields */}
          <div className="space-y-3 text-sm">
            <EditField label="Store"    editing={editMode} value={p.store || ""}
                       onChange={(v) => updateField("store", v)} testid="edit-store" />
            <EditField label="Date"     editing={editMode} value={p.date || ""}
                       type="date"
                       onChange={(v) => updateField("date", v)} testid="edit-date" />
            <EditField label="Currency" editing={editMode} value={p.currency || "USD"}
                       onChange={(v) => updateField("currency", v.toUpperCase())} testid="edit-currency" />
            <EditField label="Subtotal" editing={editMode} value={p.subtotal ?? ""}
                       type="number" step="0.01"
                       onChange={(v) => updateField("subtotal", v === "" ? null : Number(v))}
                       display={money(p.subtotal, currency)} testid="edit-subtotal" />
            <EditField label="Tax"      editing={editMode} value={p.tax ?? ""}
                       type="number" step="0.01"
                       onChange={(v) => updateField("tax", v === "" ? null : Number(v))}
                       display={money(p.tax, currency)} testid="edit-tax" />
            <EditField label="Total"    editing={editMode} value={p.total ?? ""}
                       type="number" step="0.01"
                       onChange={(v) => updateField("total", v === "" ? null : Number(v))}
                       display={money(p.total, currency)} accent="amber" testid="edit-total" />
          </div>
        </div>

        {/* Line items */}
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">
              Line items · {(p.line_items || []).length}
            </div>
            {editMode && (
              <button
                onClick={addLineItem}
                className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
                data-testid="edit-add-line-item"
              >+ Add row</button>
            )}
          </div>
          <div className="rounded-lg border border-neutral-800 divide-y divide-neutral-900 max-h-[40vh] overflow-y-auto">
            {(p.line_items || []).map((li, i) => (
              <LineItemRow
                key={i}
                idx={i}
                item={li}
                editing={editMode}
                currency={currency}
                onChange={updateLineItem}
                onRemove={() => removeLineItem(i)}
              />
            ))}
            {(p.line_items || []).length === 0 && (
              <div className="px-3 py-6 text-center text-neutral-500 text-xs">No line items.</div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-neutral-800 px-5 py-3 flex items-center gap-2">
          {!editMode ? (
            <>
              <button
                onClick={onDelete}
                className="text-xs px-3 py-2 rounded-lg bg-red-950/40 hover:bg-red-900/50 border border-red-500/40 text-red-200"
                data-testid="receipt-detail-delete"
              >Delete</button>
              <div className="flex-1" />
              <button
                onClick={onEdit}
                className="text-xs px-3 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 font-bold"
                data-testid="receipt-detail-edit"
              >✏️ Edit & Resave</button>
            </>
          ) : (
            <>
              <button
                onClick={onCancelEdit}
                className="text-xs px-3 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 border border-neutral-700"
                data-testid="receipt-edit-cancel"
              >Cancel</button>
              <div className="flex-1" />
              <button
                onClick={onSave}
                className="text-xs px-4 py-2 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-black tracking-wide hover:brightness-110"
                data-testid="receipt-edit-save"
              >💾 Save Corrections</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EditField({ label, value, editing, type = "text", step, onChange, display, accent, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 min-w-[80px]">{label}</div>
      {editing ? (
        <input
          type={type}
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1 rounded-md bg-black/50 border border-neutral-800 text-sm text-neutral-100 focus:border-amber-500/60 focus:outline-none"
          data-testid={testid}
        />
      ) : (
        <div className={`flex-1 truncate ${accent === "amber" ? "text-amber-300 font-black text-lg" : "text-neutral-200"}`}>
          {display ?? (value || "—")}
        </div>
      )}
    </div>
  );
}

function LineItemRow({ idx, item, editing, currency, onChange, onRemove }) {
  const icon = CATEGORY_ICONS[item.category || "other"] || CATEGORY_ICONS.other;
  if (!editing) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs" data-testid={`li-row-${idx}`}>
        <span className="w-5 text-center">{icon}</span>
        <span className="flex-1 truncate">{item.name}</span>
        <span className="text-neutral-500">×{item.qty || 1}</span>
        <span className="text-[10px] uppercase text-neutral-500 min-w-[70px] text-right">{item.category || "other"}</span>
        <span className="text-amber-300 font-mono min-w-[60px] text-right">
          {new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(item.price || 0))}
        </span>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-12 gap-1 px-2 py-1 text-xs items-center" data-testid={`li-edit-${idx}`}>
      <input
        className="col-span-5 px-2 py-1 rounded bg-black/50 border border-neutral-800"
        value={item.name || ""}
        onChange={(e) => onChange(idx, "name", e.target.value)}
        placeholder="Item name"
      />
      <input
        type="number" min="1" step="1"
        className="col-span-1 px-1 py-1 rounded bg-black/50 border border-neutral-800 text-center"
        value={item.qty ?? 1}
        onChange={(e) => onChange(idx, "qty", Number(e.target.value) || 1)}
      />
      <select
        className="col-span-3 px-1 py-1 rounded bg-black/50 border border-neutral-800 text-[11px]"
        value={item.category || "other"}
        onChange={(e) => onChange(idx, "category", e.target.value)}
      >
        {Object.keys(CATEGORY_ICONS).map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <input
        type="number" step="0.01"
        className="col-span-2 px-1 py-1 rounded bg-black/50 border border-neutral-800 text-right"
        value={item.price ?? 0}
        onChange={(e) => onChange(idx, "price", Number(e.target.value) || 0)}
      />
      <button
        onClick={onRemove}
        className="col-span-1 text-red-300 hover:text-red-200"
        title="Remove line item"
        data-testid={`li-remove-${idx}`}
      >×</button>
    </div>
  );
}
